<template>
  <div class="instagram-instantwin-prize-table">
    <div class="table-area" v-loading="hasCopyInProgress()">
      <el-table class="mb-x2" :data="prizes" stripe @header-click="headerClick" empty-text=" " style="width: 100%">
        <el-table-column :label="'応募グループ名'" sort-by="name" :render-header="renderHeader" min-width="15%">
          <template slot-scope="scope">
            <div class="link" @click="openCampaignApplicant(scope.row.id)">{{ scope.row.name }}</div>
          </template>
        </el-table-column>

        <el-table-column :label="'応募キーワード'" sort-by="entryKeyword" :render-header="renderHeader" min-width="35%">
          <template slot-scope="scope" v-if="getKeywordText(scope.row.entryKeyword)">
            <span class="text-disabled mr-x1">{{ '全て含む' }}</span>
            <span>{{ getKeywordText(scope.row.entryKeyword) }}</span>
          </template>
        </el-table-column>

        <el-table-column :label="'応募者数'" sort-by="-applicantCount" :render-header="renderHeader" min-width="15%">
          <template slot-scope="scope">
            <div>{{ formatNumber(scope.row.applicantCount) }}</div>
          </template>
        </el-table-column>

        <el-table-column :label="'当選者数'" sort-by="-winnerCount" :render-header="renderHeader" min-width="25%">
          <template slot-scope="scope">
            <div>{{ formatNumber(scope.row.winnerCount) }}</div>
            <div class="sub-value">
              {{ '残り' }}:&nbsp;{{ formatNumber(scope.row.maxWinnerCount - scope.row.winnerCount) }}
            </div>
          </template>
        </el-table-column>
        <el-table-column width="280">
          <template slot-scope="scope">
            <div class="action-buttons" layout layout-align="end">
              <el-tooltip :enterable="false" placement="top" :content="'編集'">
                <el-button
                  class="el-button--icon"
                  type="default"
                  :disabled="isOperationDisabled()"
                  @click="openEditDialog(scope.row.id)"
                >
                  <i class="mdi mdi-table-edit" />
                </el-button>
              </el-tooltip>
              <el-tooltip :enterable="false" placement="top" :content="'複製'">
                <el-button
                  class="el-button--icon ml-x1"
                  type="default"
                  :loading="isCopying(scope.row.id)"
                  :disabled="isCopyButtonDisabled(scope.row.id)"
                  @click="copyPrize(scope.row.id)"
                >
                  <i class="mdi mdi-content-copy" />
                </el-button>
              </el-tooltip>
              <el-tooltip :enterable="false" placement="top" :content="'シナリオ編集'">
                <el-button
                  class="el-button--icon ml-x1"
                  type="default"
                  :disabled="isOperationDisabled()"
                  @click="openScenarioEditor(scope.row.id)"
                >
                  <i class="mdi mdi-forum-outline" />
                </el-button>
              </el-tooltip>
              <el-tooltip :enterable="false" placement="top" :content="'DMテスト'">
                <el-button
                  class="el-button--icon ml-x1"
                  type="default"
                  :disabled="hasCopyInProgress()"
                  @click="openPretestDialog(scope.row.id)"
                >
                  <i class="mdi mdi-email-search-outline" />
                </el-button>
              </el-tooltip>
              <el-tooltip :enterable="false" placement="top" :content="'プレビュー'">
                <el-button
                  class="el-button--icon ml-x1"
                  type="default"
                  :disabled="isOperationDisabled()"
                  @click="openPreviewDialog(scope.row.id)"
                >
                  <i class="mdi mdi-comment-check-outline" />
                </el-button>
              </el-tooltip>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <div layout layout-align="space-between-center">
        <pagination
          flex="1"
          type="instagram-instantwin"
          :total="options.total"
          :pagingNo="options.pagingNo"
          :limitCount="options.limitCount"
          :onChangePagingNo="handleChangePagingNo"
          :onChangeLimitCount="handleChangeLimitCount"
        />
      </div>
    </div>

    <!-- 応募グループ編集モーダル -->
    <instagram-instantwin-prize-edit-dialog
      :visible="isEditDialogVisible"
      :prize-id="editingPrizeId"
      :campaign-id="campaignId"
      :prize-count="prizes.length"
      :campaign-status="campaignStatus"
      @close="closeEditDialog"
      @saved="onSaved"
    />

    <!-- シナリオエディタモーダル -->
    <instagram-scenario-editor-dialog
      :visible="isScenarioEditorVisible"
      :prize-id="editingScenarioPrizeId"
      :campaign-status="campaignStatus"
      @close="closeScenarioEditor"
      @saved="onScenarioSaved"
    />

    <InstagramDmTestDialog
      v-if="activePretestPrizeId !== null"
      :visible="isPretestDialogVisible"
      :prize-id="activePretestPrizeId"
      :campaign-status="campaignStatus"
      :has-running-campaign-on-same-account="hasRunningCampaignOnSameAccount"
      :on-refresh="onRefresh"
      @close="closePretestDialog"
    />

    <InstagramScenarioPreviewDialog
      v-if="previewingPrizeId !== null"
      :visible="isPreviewDialogVisible"
      :prize-id="previewingPrizeId"
      @close="closePreviewDialog"
    />
  </div>
</template>

<script setup lang="ts">
// @ts-nocheck TODO: 既存の型エラーを解消したら外す
import { getCurrentInstance, ref } from 'vue'

import Pagination from '@/client/components/_atoms/pagination/pagination.vue'
import { CAMPAIGN_STATUS } from '@/client/constant'
import Util from '@/client/core/util'
import { formatNumber } from '@/client/filters/filters'
import { TrackingService } from '@/client/services'
import type { TPrize } from '@/typings/api/instagram_instantwin_prizes.schema'

import { postInstagramInstantwinPrizeCopy } from '../api/postInstagramInstantwinPrizeCopy'
import { buildInstantwinPrizeKeywordText } from '../utils/prizeKeyword'
import InstagramDmTestDialog from './InstagramDmTestDialog/InstagramDmTestDialog.vue'
import InstagramInstantwinPrizeEditDialog from './InstagramInstantwinPrizeEditDialog.vue'
import InstagramScenarioEditorDialog from './InstagramScenarioEditorDialog/InstagramScenarioEditorDialog.vue'
import InstagramScenarioPreviewDialog from './InstagramScenarioPreviewDialog/InstagramScenarioPreviewDialog.vue'

interface Props {
  prizes: TPrize[]
  campaignId: number
  campaignStatus?: number
  hasRunningCampaignOnSameAccount?: boolean
  canEditCampaign?: boolean
  options: {
    total: number
    pagingNo: number
    limitCount: number
    sortBy: string
  }
  onChangePagingNo: (page: number) => void
  onChangeLimitCount: (limitCount: number) => void
  onChangeSortBy: (sortBy: string) => void
  onOpenApplicant: (prizeId: number) => void
  onRefresh: () => Promise<void> | void
}

const props = defineProps<Props>()
const { proxy } = getCurrentInstance() as {
  proxy: {
    $notify: (arg: { title: string; customClass: string; duration: number }) => void
  }
}

// モーダル管理用の状態
const isEditDialogVisible = ref(false)
const editingPrizeId = ref<number | null>(null)
const isScenarioEditorVisible = ref(false)
const editingScenarioPrizeId = ref<number | null>(null)
const isPretestDialogVisible = ref(false)
const activePretestPrizeId = ref<number | null>(null)
const isPreviewDialogVisible = ref(false)
const previewingPrizeId = ref<number | null>(null)
const copyingPrizeId = ref<number | null>(null)
type TableHeaderColumn = {
  label: string
  sortBy?: string
}

/**
 * ヘッダーをクリックするとソートする
 */
function headerClick(column: TableHeaderColumn) {
  if (isOperationDisabled()) {
    return
  }

  switch (column.label) {
    case '応募グループ名':
      TrackingService.sendEvent('sort:応募グループ一覧（IGIW）|応募グループ名')
      break
    case '応募キーワード':
      TrackingService.sendEvent('sort:応募グループ一覧（IGIW）|応募キーワード')
      break
    case '応募者数':
      TrackingService.sendEvent('sort:応募グループ一覧（IGIW）|応募者数')
      break
    case '当選者数':
      TrackingService.sendEvent('sort:応募グループ一覧（IGIW）|当選者数')
      break
    default:
      break
  }

  if (!column.sortBy) {
    return
  }

  if (column.sortBy.indexOf('-') === 0) {
    if (props.options.sortBy === column.sortBy) {
      props.onChangeSortBy(column.sortBy.substring(1))
    } else {
      props.onChangeSortBy(column.sortBy)
    }
  } else {
    if (props.options.sortBy === column.sortBy) {
      props.onChangeSortBy('-' + column.sortBy)
    } else {
      props.onChangeSortBy(column.sortBy)
    }
  }
}

/**
 * ヘッダー描画
 */
function renderHeader(createElement: (...args: unknown[]) => unknown, { column }: { column: TableHeaderColumn }) {
  return Util.renderHeader(createElement, column.label, Util.getSortClass(props.options.sortBy, column.sortBy))
}

function getKeywordText(prizeEntryKeyword: string[] | null | undefined) {
  return buildInstantwinPrizeKeywordText({
    prizeEntryKeyword
  })
}

/**
 * 応募者一覧を開く
 */
function openCampaignApplicant(id: number) {
  if (isOperationDisabled()) {
    return
  }

  TrackingService.sendEvent('click:応募グループ一覧（IGIW）|グループ名')
  props.onOpenApplicant(id)
}

/**
 * 編集ダイアログを開く
 */
function openEditDialog(prizeId: number) {
  if (isOperationDisabled()) {
    return
  }

  TrackingService.sendEvent('click:応募グループ一覧（IGIW）|編集')
  editingPrizeId.value = prizeId
  isEditDialogVisible.value = true
}

/**
 * 新規作成ダイアログを開く
 */
function openCreateDialog() {
  editingPrizeId.value = null
  isEditDialogVisible.value = true
}

/**
 * 編集ダイアログを閉じる
 */
function closeEditDialog() {
  isEditDialogVisible.value = false
  editingPrizeId.value = null
}

/**
 * 保存成功時の処理
 */
function onSaved(mode: 'create' | 'edit') {
  closeEditDialog()
  if (mode === 'edit') {
    props.onRefresh()
  }
}

/**
 * シナリオエディタを開く
 */
function openScenarioEditor(prizeId: number) {
  if (isOperationDisabled()) {
    return
  }

  TrackingService.sendEvent('click:応募グループ一覧（IGIW）|シナリオ編集')
  editingScenarioPrizeId.value = prizeId
  isScenarioEditorVisible.value = true
}

function openPretestDialog(prizeId: number) {
  if (hasCopyInProgress()) {
    return
  }

  TrackingService.sendEvent('click:応募グループ一覧（IGIW）|テスト')
  activePretestPrizeId.value = prizeId
  isPretestDialogVisible.value = true
}

function isBeforeEventCampaign() {
  return props.campaignStatus === CAMPAIGN_STATUS.BEFORE_EVENT
}

function hasCopyInProgress() {
  return copyingPrizeId.value !== null
}

function isCopying(prizeId: number) {
  return copyingPrizeId.value === prizeId
}

function isOperationDisabled() {
  return hasCopyInProgress()
}

function isCopyButtonDisabled(prizeId: number) {
  return props.canEditCampaign === false || !isBeforeEventCampaign() || hasCopyInProgress() || isCopying(prizeId)
}

function handleChangePagingNo(page: number) {
  if (isOperationDisabled()) {
    return
  }
  props.onChangePagingNo(page)
}

function handleChangeLimitCount(limitCount: number) {
  if (isOperationDisabled()) {
    return
  }
  props.onChangeLimitCount(limitCount)
}

async function copyPrize(prizeId: number) {
  if (isCopyButtonDisabled(prizeId)) {
    return
  }

  TrackingService.sendEvent('click:応募グループ一覧（IGIW）|複製')
  copyingPrizeId.value = prizeId

  try {
    const response = await postInstagramInstantwinPrizeCopy(prizeId)
    const errorType = 'error' in response ? response.error.type : null
    if (errorType || !response.data) {
      let message = '応募グループの複製に失敗しました。'
      if (errorType === 'PERMISSION_DENIED') {
        message = '応募グループを複製する権限がありません。'
      } else if (errorType === 'NOT_EXISTS') {
        message = '複製対象の応募グループが存在しません。'
      } else if (errorType === 'CAMPAIGN_STATUS_INVALID') {
        message = '開始前のみ実行可能です（キャンペーン中/終了後は複製できません）。'
      }

      proxy.$notify({
        title: message,
        customClass: 'danger',
        duration: 5000
      })
      return
    }

    proxy.$notify({
      title: '複製が完了しました。',
      customClass: 'success',
      duration: 5000
    })
    await props.onRefresh()
  } catch {
    proxy.$notify({
      title: '応募グループの複製に失敗しました。',
      customClass: 'danger',
      duration: 5000
    })
  } finally {
    copyingPrizeId.value = null
  }
}

/**
 * シナリオエディタを閉じる
 */
function closeScenarioEditor() {
  isScenarioEditorVisible.value = false
  editingScenarioPrizeId.value = null
}

/**
 * シナリオ保存成功時の処理
 */
function onScenarioSaved() {
  closeScenarioEditor()
}

function closePretestDialog() {
  isPretestDialogVisible.value = false
  activePretestPrizeId.value = null
}

function openPreviewDialog(prizeId: number) {
  if (isOperationDisabled()) {
    return
  }

  TrackingService.sendEvent('click:応募グループ一覧（IGIW）|プレビュー')
  previewingPrizeId.value = prizeId
  isPreviewDialogVisible.value = true
}

function closePreviewDialog() {
  isPreviewDialogVisible.value = false
  previewingPrizeId.value = null
}

defineExpose({
  openCreateDialog
})
</script>

<style lang="scss" scoped>
.instagram-instantwin-prize-table {
  .table-area {
    position: relative;
  }

  .action-buttons {
    white-space: nowrap;
  }

  .sub-value {
    margin-top: 4px;
    font-size: 12px;
    color: #999;
  }
}
</style>
